// src/components/tokenPage/derivatives/ExchangeFundingBreakdownCard.tsx

import React from "react";
import { EXCHANGE_COLORS } from "@/constants/coinglass/coinglass";
import { capitalizeExchange } from "@/utils/coinglass";
import { useTokenDerivatives } from "@/contexts/token";
import { DataCard } from "@/components/ui";

interface Props {
  ticker: string;
}

const ExchangeFundingBreakdownCard: React.FC<Props> = () => {
  const { exchangeRates, isLoading } = useTokenDerivatives();

  // Hide if no data available (after loading)
  if (!isLoading && exchangeRates.length === 0) {
    return null;
  }

  return (
    <DataCard
      title="Funding Rate by Exchange"
      subtitle="Current funding rate snapshot across exchanges"
      icon="list"
      iconColor="text-blue-400"
      isLoading={isLoading}
      loadingVariant="stats"
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {exchangeRates.map(({ exchange, rate }) => {
          const tone = rate > 0 ? 'text-emerald-400' : rate < 0 ? 'text-rose-400' : 'text-slate-300';
          const bg = rate > 0 ? 'bg-emerald-500/10' : rate < 0 ? 'bg-rose-500/10' : 'bg-gray-700/40';
          const colorDot = EXCHANGE_COLORS[exchange] ?? '#9CA3AF';
          const abs = Math.abs(rate);
          const decimals = abs < 0.01 ? 4 : 3;
          return (
            <div key={exchange} className={`rounded-lg px-3 py-2 ${bg}`}>
              <div className="text-[11px] text-slate-400 flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: colorDot }} />
                {capitalizeExchange(exchange)}
              </div>
              <div className={`text-sm font-semibold tabular-nums ${tone}`}>
                {rate.toFixed(decimals)}%
              </div>
            </div>
          );
        })}
        {exchangeRates.length === 0 && (
          <div className="text-sm text-slate-400">No exchange data available.</div>
        )}
      </div>
    </DataCard>
  );
};

export default ExchangeFundingBreakdownCard;
